"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"

import { HoneypotField } from "@/components/marketing/honeypot-field"
import { useMarketingAuth } from "@/components/marketing/use-marketing-auth"
import { AnalyticsEvent, stitchLeadEmail, track } from "@/lib/analytics"
import { withHoneypot } from "@/lib/honeypot"
import { cn, EMAIL_RE } from "@/lib/utils"
import { ReadyCheckout } from "../../../pay/embedded/_components/checkout-client"
import { stripePromise, type OfferCopy } from "../../../pay/embedded/_components/checkout-shared"

// The membership checkout, monthly or yearly, whichever the page was opened
// for (`planId`, resolved by page.tsx).
//
// Once the email step below has run, this page mounts the Payment Element form
// /pay/embedded builds (`ReadyCheckout`), confirmed against the first invoice
// of the membership subscription the route opens. It is never Stripe's own
// iframe checkout, which can never be made to match it.
//
// A signed-out visitor types his email first: the subscription opens for that
// address alone, and the webhook finds or makes his account from it once the
// first invoice is paid (startCommunityCheckout says why nothing about an
// account is read before then). A signed-in visitor skips the step, and his
// checkout runs the already-a-member guard on his own account.

const SECTION = "mx-auto max-w-[46rem] px-5 sm:px-8"
// The /pay/embedded page frame, so the payment step here is that page's
// layout exactly, not a narrower copy of it.
const PAY_SECTION = "mx-auto max-w-[92rem] px-5 sm:px-8 lg:px-10"

type ReadyState = {
  clientSecret: string
  paymentIntentId: string
  offer: OfferCopy
  amountCents: number
  currency: string
  prefillEmail: string | null
  recurringInterval: string | null
}

type Phase =
  | { status: "email" }
  | { status: "loading" }
  | { status: "ready"; checkout: ReadyState }
  | { status: "error"; message: string; alreadyMember?: boolean }

export default function CommunityJoinClient({ planId }: { planId: string }) {
  const { authed } = useMarketingAuth()
  const [phase, setPhase] = useState<Phase>({ status: "email" })
  const [email, setEmail] = useState("")
  const [fieldError, setFieldError] = useState("")

  // `form` is the submitted email form, so the spam trap (lib/honeypot.ts)
  // rides along; the signed-in auto start has no form and sends none.
  const start = useCallback(async (typedEmail: string | null, form: EventTarget | null = null) => {
    if (!stripePromise) {
      setPhase({ status: "error", message: "Could not open the checkout. Try again in a moment." })
      return
    }
    setPhase({ status: "loading" })
    try {
      const res = await fetch("/api/community/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withHoneypot(typedEmail ? { email: typedEmail, planId } : { planId }, form)),
      })
      const data = (await res.json().catch(() => ({}))) as {
        clientSecret?: string
        paymentIntentId?: string
        amount?: number
        currency?: string
        offer?: OfferCopy
        recurringInterval?: string | null
        email?: string | null
        error?: string
        alreadyMember?: boolean
      }
      if (!res.ok || !data.clientSecret || !data.paymentIntentId || !data.offer || data.amount == null) {
        setPhase({
          status: "error",
          message: data.error || "Could not open the checkout. Try again in a moment.",
          alreadyMember: data.alreadyMember,
        })
        return
      }
      if (typedEmail) {
        stitchLeadEmail(typedEmail)
        track(AnalyticsEvent.NewsletterSignup, { source: "community-membership" })
      }
      setPhase({
        status: "ready",
        checkout: {
          clientSecret: data.clientSecret,
          paymentIntentId: data.paymentIntentId,
          offer: data.offer,
          amountCents: data.amount,
          currency: data.currency ?? "usd",
          prefillEmail: data.email ?? typedEmail,
          recurringInterval: data.recurringInterval ?? null,
        },
      })
    } catch {
      setPhase({ status: "error", message: "Could not open the checkout. Try again in a moment." })
    }
  }, [planId])

  // A signed-in visitor never retypes an address we already hold: the moment
  // the browser auth probe settles to signed-in, his checkout opens on the
  // session's own email.
  //
  // The once-only latch is a ref rather than state on purpose. `start()`
  // already drives every render this needs, and a second setState in the
  // effect body would only add a cascading render for a value nothing renders.
  const autoStarted = useRef(false)
  useEffect(() => {
    if (!authed || autoStarted.current) return
    autoStarted.current = true
    void start(null)
  }, [authed, start])

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const normalized = email.trim().toLowerCase()
    if (!EMAIL_RE.test(normalized)) {
      setFieldError("Enter a valid email so we can set up your account.")
      return
    }
    setFieldError("")
    void start(normalized, form)
  }

  // The payment step. ReadyCheckout brings its own confirmatory heading (the
  // membership's copy carries this page's own line, word for word), so the
  // intro below would only repeat it.
  if (phase.status === "ready") {
    const ready = phase.checkout
    return (
      <section className="tone-paper">
        <div className={cn(PAY_SECTION, "py-8 md:py-10")}>
          <div className="mx-auto max-w-[62rem]">
            <ReadyCheckout
              key={ready.clientSecret}
              clientSecret={ready.clientSecret}
              paymentIntentId={ready.paymentIntentId}
              offer={ready.offer}
              amountCents={ready.amountCents}
              currency={ready.currency}
              prefillEmail={ready.prefillEmail}
              // Our PayPal button captures a one-off order, which would pay
              // the first charge and leave the membership with nothing to
              // renew on, and no code ever discounts a recurring price.
              paypalReady={false}
              recurringInterval={ready.recurringInterval}
              promo={null}
            />
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="tone-paper">
      <div className={cn(SECTION, "py-12 md:py-16")}>
        {/* The same intro as the /pay checkout: a centered confirmatory
            heading and a single what-it-is line. No eyebrow, no secure marker
            (that lives in the checkout masthead), and no price in the intro,
            because the payment form that follows states the amount and the
            interval. */}
        <div className="mx-auto max-w-[52rem] pb-7 text-center md:pb-8">
          <h1 className="serious-display mx-auto max-w-[24ch] text-balance text-[clamp(1.7rem,3.4vw,2.4rem)] leading-[1.02] text-[var(--color-ink)]">
            You&apos;re joining the Cold Approach community.
          </h1>
          <p className="mx-auto mt-2.5 max-w-[52ch] text-[0.96rem] leading-[1.55] text-[var(--color-ink-muted)]">
            Your card details go straight to Stripe. We never see or store them.
          </p>
        </div>

        <div className="mt-8">
          {phase.status === "email" ? (
            <form method="post" onSubmit={onSubmit} className="relative mx-auto max-w-[26rem] rounded-[var(--radius-xl)] border border-[var(--color-rule)] bg-[var(--color-paper)] p-6 shadow-[var(--glass-shadow-2)]">
              <HoneypotField />
              <label
                htmlFor="community-email"
                className="block text-[0.95rem] font-semibold text-[var(--color-ink)]"
              >
                Email
              </label>
              <p className="mt-1 text-[0.85rem] leading-[1.5] text-[var(--color-ink-soft)]">
                We set up your account with this address and send your Slack invite here.
              </p>
              <input
                id="community-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (fieldError) setFieldError("")
                }}
                placeholder="you@email.com"
                className="mt-3 min-h-12 w-full rounded-[10px] border border-[var(--color-rule-strong)] bg-[var(--color-paper-high)] px-4 text-[16px] text-[var(--color-ink)] outline-none transition-colors focus:border-[var(--color-vermilion)]"
              />
              <button
                type="submit"
                className="mt-3 inline-flex min-h-12 w-full items-center justify-center rounded-[10px] bg-[var(--color-vermilion)] px-6 text-[0.98rem] font-semibold text-white transition-colors hover:bg-[var(--color-oxblood)]"
              >
                Continue to payment
              </button>
              {fieldError ? (
                <p className="mt-2.5 text-[0.85rem] text-[var(--color-vermilion)]">{fieldError}</p>
              ) : null}
            </form>
          ) : phase.status === "error" ? (
            <div className="max-w-[32rem] rounded-[var(--radius-xl)] border border-[var(--color-vermilion)] bg-[var(--color-paper)] p-6">
              <p className="text-[0.95rem] leading-[1.6] text-[var(--color-ink)]">{phase.message}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {phase.alreadyMember ? (
                  <Link
                    href="/app/community"
                    className="inline-flex min-h-11 items-center justify-center rounded-[10px] bg-[var(--color-vermilion)] px-5 text-[0.95rem] font-semibold text-white transition-colors hover:bg-[var(--color-oxblood)]"
                  >
                    Open your channels
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPhase({ status: "email" })}
                    className="inline-flex min-h-11 items-center justify-center rounded-[10px] bg-[var(--color-vermilion)] px-5 text-[0.95rem] font-semibold text-white transition-colors hover:bg-[var(--color-oxblood)]"
                  >
                    Try again
                  </button>
                )}
                <Link
                  href="/community"
                  className="inline-flex min-h-11 items-center justify-center rounded-[10px] border border-[var(--color-rule-strong)] px-5 text-[0.95rem] font-semibold text-[var(--color-ink)] transition-colors hover:border-[var(--color-ink)]"
                >
                  Back to the community
                </Link>
              </div>
            </div>
          ) : (
            <p className="text-[0.95rem] text-[var(--color-ink-muted)]">Opening your checkout…</p>
          )}
        </div>
      </div>
    </section>
  )
}
